import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface PageTocHeading {
  id: string;
  text: string;
  /** 2 = top-level entry, 3 = indented sub-entry. */
  level: number;
}

/**
 * "On this page" navigation for a long-form article. Entries scroll their heading into view and the
 * one currently at the top of the viewport is highlighted as the reader moves down the page.
 *
 * Headings are resolved by id against the rendered document, so the same component serves both
 * Angular-rendered sections and a body injected through `[innerHTML]`.
 */
@Component({
  selector: 'app-page-toc',
  standalone: true,
  template: `
    <p class="toc-title">{{ title }}</p>
    <ul>
      @for (heading of headings; track heading.id) {
        <li [class.is-sub]="heading.level >= 3">
          <button
            type="button"
            [class.is-active]="heading.id === activeId()"
            (click)="scrollTo(heading)"
          >
            {{ heading.text }}
          </button>
        </li>
      }
    </ul>
  `,
  styles: [
    `
      /* A panel, not a bare list: on a tinted reading surface the entries need their own sheet to
         read as navigation rather than as part of the article. */
      :host {
        display: block;
        position: sticky;
        top: 12px;
        align-self: start;
        max-height: calc(100vh - 130px);
        overflow-y: auto;
        padding: 14px 12px;
        border: 1px solid var(--border-soft, #e6ebf1);
        border-radius: 14px;
        background: var(--bg-card, #fff);
      }
      @media (max-width: 900px) {
        :host {
          position: static;
          max-height: none;
        }
      }
      .toc-title {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 10px;
        padding-left: 2px;
        font-size: 10px;
        font-weight: var(--fw-extrabold, 800);
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--fg-3, #6b7280);
      }
      .toc-title::before {
        content: '';
        width: 14px;
        height: 2px;
        border-radius: 2px;
        background: var(--h4c-gradient-secondary, linear-gradient(90deg, #262262, #27aae1));
      }
      ul {
        margin: 0;
        padding: 0;
        list-style: none;
        box-shadow: inset 1px 0 0 var(--border-default, #e5e7eb);
      }
      li.is-sub {
        padding-left: 12px;
      }
      button {
        display: block;
        width: 100%;
        padding: 6px 8px 6px 11px;
        border: 0;
        border-left: 2px solid transparent;
        border-radius: 0 8px 8px 0;
        background: transparent;
        text-align: left;
        font: inherit;
        font-size: var(--text-base, 0.8125rem);
        line-height: 1.35;
        color: var(--fg-3, #6b7280);
        cursor: pointer;
        transition:
          background 0.14s,
          color 0.14s;
      }
      button:hover {
        background: var(--h4c-dark-blue-05, rgba(38, 34, 98, 0.05));
        color: var(--h4c-dark-blue, #262262);
      }
      button.is-active {
        border-left-color: var(--h4c-bright-blue, #27aae1);
        background: linear-gradient(
          90deg,
          var(--h4c-bright-blue-14, rgba(39, 170, 225, 0.14)),
          rgba(39, 170, 225, 0)
        );
        font-weight: var(--fw-semibold, 600);
        color: var(--h4c-dark-blue, #262262);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageTocComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) headings: PageTocHeading[] = [];
  @Input() title = 'On this page';

  readonly activeId = signal('');

  private readonly document = inject(DOCUMENT);

  private observer?: IntersectionObserver;
  /** Guards the deferred arm below against a headings change that lands while it is still queued. */
  private armToken = 0;

  ngOnChanges(): void {
    this.activeId.set(this.headings[0]?.id ?? '');
    this.arm();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  scrollTo(heading: PageTocHeading): void {
    this.target(heading.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.activeId.set(heading.id);
  }

  private target(id: string): HTMLElement | null {
    return this.document.getElementById(id);
  }

  /** Deferred: the headings arrive with the body, which has not been laid out yet on this tick. */
  private arm(): void {
    this.observer?.disconnect();
    if (typeof IntersectionObserver === 'undefined') return;

    this.armToken += 1;
    const token = this.armToken;
    setTimeout(() => {
      if (token !== this.armToken) return;

      const targets = this.headings
        .map((heading) => this.target(heading.id))
        .filter((target): target is HTMLElement => !!target);
      if (!targets.length) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
          if (visible?.target.id) {
            this.activeId.set(visible.target.id);
          }
        },
        { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
      );

      targets.forEach((target) => this.observer?.observe(target));
    });
  }
}
